import React, { useState, useRef, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { SearchBar, Popup } from 'antd-mobile'
import { useIsMobile } from '@/hooks/useIsMobile'

/**
 * 搜索选项接口
 */
export interface SearchOption {
  id?: string
  name?: string
  label?: string
  value?: string
  payload?: Record<string, any>
  commonName?: string
  domain?: string
  displayNumber?: string
  department?: string
  departmentName?: string
  avatarUrl?: string
  jobStatus?: {
    code: string
    name: string
  }
  leader?: string
  code?: string
  provinceCode?: string
  provinceName?: string
  [key: string]: any
}

interface AsyncSearchSelectResponsiveProps<T extends SearchOption = SearchOption> {
  label: string
  value: string
  displayText: string
  onSearch: (keyword: string) => Promise<T[]>
  onChange: (value: string, label: string, option?: T) => void
  placeholder?: string
  required?: boolean
  className?: string
  formatOption?: (option: T) => React.ReactNode
  loadDefaultOptions?: () => Promise<T[]>
  disabled?: boolean
}

/**
 * 响应式异步搜索下拉组件
 * PC端：保持原有 portal 下拉框
 * 移动端：使用 antd-mobile Popup + SearchBar
 */
const AsyncSearchSelectResponsive = React.forwardRef<
  HTMLDivElement,
  AsyncSearchSelectResponsiveProps
>(
  (
    {
      label,
      // @ts-ignore - value is received but not used, kept for interface compatibility
      value,
      displayText,
      onSearch,
      onChange,
      placeholder = '输入关键字搜索...',
      required = false,
      className = '',
      formatOption,
      loadDefaultOptions,
      disabled = false,
    },
    _ref
  ) => {
  const isMobile = useIsMobile()

  // 移动端状态
  const [mobilePopupVisible, setMobilePopupVisible] = useState(false)
  const [mobileSearchValue, setMobileSearchValue] = useState('')

  // PC端状态
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [options, setOptions] = useState<SearchOption[]>([])
  const [inputValue, setInputValue] = useState(displayText)
  const [error, setError] = useState<string | null>(null)
  const [defaultLoadFailed, setDefaultLoadFailed] = useState(false)
  const [justSelected, setJustSelected] = useState(false)
  const [isUserTyping, setIsUserTyping] = useState(false)
  const [hasUserModified, setHasUserModified] = useState(false)

  // Refs
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const defaultOptionsCache = useRef<SearchOption[] | null>(null)

  // 下拉框 portal 定位
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    })
  }, [])

  // 处理搜索（带防抖）
  const handleSearch = useCallback(
    async (keyword: string) => {
      if (isMobile) {
        setMobileSearchValue(keyword)
      } else {
        setInputValue(keyword)
      }

      if (keyword.trim() === '') {
        if (defaultOptionsCache.current) {
          setOptions(defaultOptionsCache.current)
        } else {
          setOptions([])
        }
        setError(null)
        return
      }

      setIsLoading(true)
      setError(null)
      setDefaultLoadFailed(false)

      try {
        const results = await onSearch(keyword)
        setOptions(results)
      } catch (err) {
        setError('搜索失败，请稍后重试')
        setOptions([])
      } finally {
        setIsLoading(false)
      }
    },
    [onSearch, isMobile]
  )

  // 处理输入变化（防抖 300ms）
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyword = e.target.value
    setInputValue(keyword)
    setIsUserTyping(true)
    setJustSelected(false)

    if (keyword !== displayText) {
      setHasUserModified(true)
      if (keyword.trim() === '' && displayText) {
        onChange('', '', undefined)
      } else if (displayText && keyword.trim() !== '') {
        onChange('', '', undefined)
      }
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      handleSearch(keyword)
      setIsUserTyping(false)
    }, 300)
  }

  // 移动端搜索变化
  const handleMobileSearchChange = (keyword: string) => {
    setMobileSearchValue(keyword)

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      handleSearch(keyword)
    }, 300)
  }

  // 处理选项点击
  const handleSelectOption = (option: SearchOption) => {
    let value = ''
    let label = ''

    if (option.commonName) {
      value = option.domain || option.displayNumber || option.id || ''
      label = option.commonName
    } else if (option.number) {
      value = option.number
      label = option.name || option.shortName || ''
    } else if (option.id || option.name) {
      value = option.id || option.code || option.value || ''
      label = option.name || option.label || ''
    } else {
      value = option.value || ''
      label = option.label || ''
    }

    onChange(value, label, option)

    if (isMobile) {
      setMobilePopupVisible(false)
      setMobileSearchValue('')
    } else {
      setInputValue(label)
      setIsOpen(false)
    }

    setOptions([])
    setJustSelected(true)
    setHasUserModified(false)
    setIsUserTyping(false)
  }

  // 处理输入框获得焦点
  const handleInputFocus = () => {
    setIsOpen(true)
    setJustSelected(false)

    if (!inputValue.trim() && loadDefaultOptions) {
      if (defaultOptionsCache.current) {
        setOptions(defaultOptionsCache.current)
      } else if (!defaultLoadFailed) {
        setIsLoading(true)
        loadDefaultOptions()
          .then((results) => {
            defaultOptionsCache.current = results
            setOptions(results)
          })
          .catch(() => {
            setDefaultLoadFailed(true)
            setOptions([])
          })
          .finally(() => {
            setIsLoading(false)
          })
      }
    } else if (!inputValue.trim()) {
      setOptions([])
    }
  }

  // 重试加载默认列表
  const retryDefaultLoad = useCallback(() => {
    if (!loadDefaultOptions) return
    defaultOptionsCache.current = null
    setDefaultLoadFailed(false)
    setIsLoading(true)
    loadDefaultOptions()
      .then((results) => {
        defaultOptionsCache.current = results
        setOptions(results)
      })
      .catch(() => {
        setDefaultLoadFailed(true)
        setOptions([])
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [loadDefaultOptions])

  // 处理输入框失焦
  const handleInputBlur = () => {
    setTimeout(() => {
      setIsOpen(false)
      setJustSelected(false)
    }, 200)
  }

  // 处理清空按钮点击
  const handleClearClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    setInputValue('')
    setOptions([])
    setIsOpen(false)
    setJustSelected(false)
    setHasUserModified(false)
    setIsUserTyping(false)

    onChange('', '', undefined)
    inputRef.current?.focus()
  }

  // 移动端打开弹窗
  const handleMobileOpen = () => {
    if (disabled) return
    setMobilePopupVisible(true)
    setMobileSearchValue('')

    // 加载默认选项
    if (loadDefaultOptions && !defaultOptionsCache.current && !defaultLoadFailed) {
      setIsLoading(true)
      loadDefaultOptions()
        .then((results) => {
          defaultOptionsCache.current = results
          setOptions(results)
        })
        .catch(() => {
          setDefaultLoadFailed(true)
          setOptions([])
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else if (defaultOptionsCache.current) {
      setOptions(defaultOptionsCache.current)
    }
  }

  // 点击外部关闭 + 打开时计算定位 + 滚动/缩放时更新定位
  useEffect(() => {
    if (isMobile) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const insideContainer = containerRef.current?.contains(target) ?? false
      const insideDropdown = dropdownRef.current?.contains(target) ?? false
      if (!insideContainer && !insideDropdown) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      updateDropdownPosition()
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', updateDropdownPosition, true)
      window.addEventListener('resize', updateDropdownPosition)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', updateDropdownPosition, true)
      window.removeEventListener('resize', updateDropdownPosition)
    }
  }, [isOpen, updateDropdownPosition, isMobile])

  // 清理 debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // 同步外部 displayText 到内部 inputValue
  useEffect(() => {
    if (!justSelected && !isUserTyping && !hasUserModified && displayText !== inputValue) {
      setInputValue(displayText)
    }
  }, [displayText, justSelected, isUserTyping, hasUserModified, inputValue])

  // 移动端渲染
  if (isMobile) {
    return (
      <div className={`relative ${className}`}>
        <label className="heading-2 mb-clay-sm block">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>

        <div
          className={`clay-input w-full flex items-center justify-between ${
            disabled ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : 'cursor-pointer'
          }`}
          onClick={handleMobileOpen}
        >
          <span className={displayText ? 'text-gray-900' : 'text-gray-400'}>
            {displayText || placeholder}
          </span>
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>

        <Popup
          visible={mobilePopupVisible}
          onMaskClick={() => setMobilePopupVisible(false)}
          position="bottom"
          bodyStyle={{
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            minHeight: '60vh',
            maxHeight: '80vh',
          }}
        >
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{label}</h3>
              <button
                onClick={() => setMobilePopupVisible(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <SearchBar
              placeholder={placeholder}
              value={mobileSearchValue}
              onChange={handleMobileSearchChange}
              style={{
                '--border-radius': '12px',
                '--background': '#f1f5f9',
                '--height': '44px',
              }}
            />

            <div className="mt-4" style={{ maxHeight: 'calc(80vh - 140px)', overflowY: 'auto' }}>
              {isLoading ? (
                <div className="p-8 text-center text-gray-600">加载中...</div>
              ) : defaultLoadFailed && !mobileSearchValue.trim() ? (
                <div className="p-8 text-center">
                  <p className="text-gray-500 mb-3">暂无{label}</p>
                  <button
                    className="text-blue-600 hover:text-blue-700 font-medium"
                    onClick={retryDefaultLoad}
                  >
                    请重试
                  </button>
                </div>
              ) : options.length > 0 ? (
                <div>
                  {options.map((option, index) => (
                    <div
                      key={`${option.value}-${index}`}
                      onClick={() => handleSelectOption(option)}
                      className="px-4 py-3 border-b border-gray-100 last:border-b-0 active:bg-gray-50"
                    >
                      {formatOption ? formatOption(option) : (
                        <p className="text-base font-medium text-gray-900">
                          {option.name || option.label || ''}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : mobileSearchValue.trim() !== '' ? (
                <div className="p-8 text-center text-gray-600">未找到匹配的结果</div>
              ) : (
                <div className="p-8 text-center text-gray-600">请输入关键字搜索</div>
              )}
            </div>
          </div>
        </Popup>
      </div>
    )
  }

  // PC端渲染（保持原有逻辑）
  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <label className="heading-2 mb-clay-sm block">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`clay-input w-full pr-20 ${disabled ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
          placeholder={disabled ? '' : placeholder}
          value={inputValue}
          onChange={disabled ? undefined : handleInputChange}
          onFocus={disabled ? undefined : handleInputFocus}
          onBlur={disabled ? undefined : handleInputBlur}
          readOnly={disabled}
          autoComplete="off"
        />

        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
          {inputValue && !disabled && (
            <button
              type="button"
              className="hover:bg-gray-100 rounded-full p-1 transition-colors"
              onClick={handleClearClick}
              onMouseDown={(e) => e.preventDefault()}
              title="清空"
            >
              <svg
                className="w-4 h-4 text-gray-500 hover:text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}

          {isLoading ? (
            <div className="animate-spin">
              <svg
                className="w-5 h-5 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          ) : (
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          )}
        </div>
      </div>

      {error && <p className="mt-2 text-clay-sm text-red-500">{error}</p>}

      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="bg-white rounded-clay border border-slate-200 shadow-clay"
          style={dropdownStyle}
        >
          {isLoading ? (
            <div className="p-4 text-center text-clay-sm text-gray-600">
              加载中...
            </div>
          ) : defaultLoadFailed && !inputValue.trim() ? (
            <div className="p-5 text-center">
              <div className="mb-2 flex justify-center">
                <svg className="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <p className="text-sm text-slate-500 mb-3">暂无{label}</p>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                onClick={retryDefaultLoad}
                onMouseDown={(e) => e.preventDefault()}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                请重试
              </button>
            </div>
          ) : options.length > 0 ? (
            <ul className="max-h-60 overflow-y-auto">
              {options.map((option, index) => (
                <li
                  key={`${option.value}-${index}`}
                  className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                  onClick={() => handleSelectOption(option)}
                >
                  {formatOption ? (
                    <div>{formatOption(option)}</div>
                  ) : (
                    <p className="text-clay-sm font-medium text-gray-800">
                      {option.name || option.label || ''}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          ) : inputValue.trim() !== '' ? (
            <div className="p-4 text-center text-clay-sm text-gray-600">
              未找到匹配的结果
            </div>
          ) : (
            <div className="p-4 text-center text-clay-sm text-gray-600">
              请输入关键字搜索
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  )
  }
)

AsyncSearchSelectResponsive.displayName = 'AsyncSearchSelectResponsive'

export default AsyncSearchSelectResponsive
